/**
 * OverlayRenderer — Displays floating content cards (text, image, bullet list)
 * as DOM overlays near the avatar. Glassmorphic style with smooth animations.
 */

const AUTO_DISMISS_MS = 15000
const CARD_BOTTOM = 320 // pixels from bottom of viewport (above avatar)

function getCardMaxWidth(): number {
  return Math.min(300, window.innerWidth - 32)
}

export class OverlayRenderer {
  private cards: HTMLDivElement[] = []

  /** Show a text card with title and body. */
  showTextCard(title: string, body: string): void {
    const card = this.createCard()

    if (title) {
      const h = document.createElement('div')
      h.textContent = title
      Object.assign(h.style, {
        fontWeight: '700',
        fontSize: '14px',
        marginBottom: '6px',
        color: '#1a1a2e',
      })
      card.appendChild(h)
    }

    if (body) {
      const p = document.createElement('div')
      p.textContent = body
      Object.assign(p.style, {
        fontSize: '13px',
        lineHeight: '1.5',
        color: '#3a3a4e',
        whiteSpace: 'pre-wrap',
      })
      card.appendChild(p)
    }

    this.mount(card)
  }

  /** Show an image card with optional caption. */
  showImageCard(imageUrl: string, caption?: string): void {
    const card = this.createCard()

    const img = document.createElement('img')
    img.src = imageUrl
    img.alt = caption ?? ''
    Object.assign(img.style, {
      width: '100%',
      borderRadius: '8px',
      marginBottom: caption ? '8px' : '0',
      display: 'block',
    })
    card.appendChild(img)

    if (caption) {
      const cap = document.createElement('div')
      cap.textContent = caption
      Object.assign(cap.style, {
        fontSize: '12px',
        color: '#6a6a7e',
        textAlign: 'center',
      })
      card.appendChild(cap)
    }

    this.mount(card)
  }

  /** Show a bullet list with title and items. */
  showBulletList(title: string, items: string[]): void {
    const card = this.createCard()

    if (title) {
      const h = document.createElement('div')
      h.textContent = title
      Object.assign(h.style, {
        fontWeight: '700',
        fontSize: '14px',
        marginBottom: '8px',
        color: '#1a1a2e',
      })
      card.appendChild(h)
    }

    const ul = document.createElement('ul')
    Object.assign(ul.style, {
      margin: '0',
      paddingLeft: '18px',
      fontSize: '13px',
      lineHeight: '1.6',
      color: '#3a3a4e',
    })
    for (const item of items) {
      const li = document.createElement('li')
      li.textContent = item
      li.style.marginBottom = '4px'
      ul.appendChild(li)
    }
    card.appendChild(ul)

    this.mount(card)
  }

  /** Dismiss all visible overlay cards. */
  dismissAll(): void {
    for (const card of [...this.cards]) {
      this.removeCard(card)
    }
  }

  // -----------------------------------------------------------------------
  // Private
  // -----------------------------------------------------------------------

  private createCard(): HTMLDivElement {
    const card = document.createElement('div')
    Object.assign(card.style, {
      position: 'fixed',
      maxWidth: `${getCardMaxWidth()}px`,
      padding: '14px 16px',
      background: 'rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(200, 208, 218, 0.6)',
      borderRadius: '16px',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.10)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      zIndex: '2147483645',
      opacity: '0',
      transform: 'translateY(12px)',
      transition: globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
        ? 'none'
        : 'opacity 0.3s ease, transform 0.3s ease',
      pointerEvents: 'auto',
    })

    // Close button (X)
    const close = document.createElement('button')
    close.textContent = '\u00D7'
    Object.assign(close.style, {
      position: 'absolute',
      top: '6px',
      right: '8px',
      background: 'none',
      border: 'none',
      fontSize: '18px',
      color: '#9a9ab0',
      cursor: 'pointer',
      lineHeight: '1',
      padding: '2px 4px',
    })
    close.addEventListener('click', () => this.removeCard(card))
    card.appendChild(close)

    return card
  }

  private mount(card: HTMLDivElement): void {
    // Position: stacked above the avatar, offset each card upward
    const offsetIndex = this.cards.length
    const bottomPos = CARD_BOTTOM + offsetIndex * 10

    card.style.bottom = `${bottomPos}px`
    card.style.left = '50%'
    card.style.transform = 'translateX(-50%) translateY(12px)'

    document.body.appendChild(card)
    this.cards.push(card)

    // Trigger slide-in animation
    requestAnimationFrame(() => {
      card.style.opacity = '1'
      card.style.transform = 'translateX(-50%) translateY(0)'
    })

    // Auto-dismiss
    const timer = setTimeout(() => this.removeCard(card), AUTO_DISMISS_MS)
    ;(card as any)._dismissTimer = timer
  }

  private removeCard(card: HTMLDivElement): void {
    clearTimeout((card as any)._dismissTimer)

    card.style.opacity = '0'
    card.style.transform = 'translateX(-50%) translateY(12px)'

    setTimeout(() => {
      card.remove()
      this.cards = this.cards.filter(c => c !== card)
    }, 300)
  }
}
